import classnames from 'classnames'
import { useMemo, useState } from 'react'
import { flushSync } from 'react-dom'

import {
    AddressInput, Alert,
    Button,
    ConfirmPopup,
    Form, FormWarningsType,
    HandleDuplicatesPopup,
    HandleUnverifiedAddressPopup,
    IconAlertCircle, IconAlertTriangle,
    IconCheckCircleBroken,
    IconHelpCircle,
    IconTrash02,
    Input, Popup,
    Table,
    Tabs, Tooltip
} from '@/components'
import { FormErrorsType } from '@/components/ui/InputError'
import {
    UploadWarrantiesFormDataType,
    UploadWarrantiesRowDataType,
    UploadWarrantyDataType
} from '@/containers/warranties/Upload'
import { useAnalytics, useAuth } from '@/hooks'
import { Company, InverterManufacturer, Warranty } from '@/models'
import {
    FormChangeEvent,
    FormChangeEventHandler,
    TableRowType,
    WarrantyBulkUploadColumnType
} from '@/types'

type StepValidateDataProps = {
    form: UploadWarrantiesFormDataType
    errors: FormErrorsType
    columns: WarrantyBulkUploadColumnType[]
    onNext: () => void
    onPrev: () => void
    onChange: FormChangeEventHandler
    processing: boolean
    company: Company
}

type TabType = 'all' | 'clean' | 'errored' | 'warned'

const StepValidateData = ({
    form,
    columns,
    errors,
    processing,
    company,
    ...props
}: StepValidateDataProps) => {
    const { trackEvent } = useAnalytics()
    const { user } = useAuth()
    const [tab, setTab] = useState<TabType>('all')
    const [showDuplicatePopup, setShowDuplicatePopup] = useState(false)
    const [showUnverifiedPopup, setShowUnverifiedPopup] = useState(false)
    const [showDeletePopup, setShowDeletePopup] = useState(false)
    const [showSystemIdPopup, setShowSystemIdPopup] = useState(false)
    const [selectedRow, setSelectedRow] = useState<UploadWarrantiesRowDataType | null>(null)
    const [focusedCell, setFocusedCell] = useState<{
        index: null | number,
        key: null | string
    }>({
        index: null,
        key: null
    })

    const errorsMemo = useMemo(() => Object.keys(errors).reduce((acc, item) => {
        const [, index, field] = item.split('.')
        return typeof index === 'undefined' ? acc : {
            ...acc,
            [index]: {
                ...acc[parseInt(index)] || {},
                [field]: errors[item]
            }
        }
    }, {} as FormErrorsType[]), [errors])

    const warningsMemo = useMemo(() => form.warranties.reduce((acc, item, index) =>
        item.inverter_name?.toLowerCase().includes('tesla') &&
        !item.system_id &&
        user?.role.isCertifiedProvider
            ? {
                ...acc,
                [index]: {
                    system_id: 'Please provide a Device Identification Number (DIN)',
                    missing_tesla_din: 'true'
                }
            }
            : acc, [] as FormWarningsType[])
    , [form.warranties])

    const handleClickBack = () => {
        props.onPrev()
        trackEvent('button_click', 'warranties', 'Bulk Enrollment Step Validate Back Button')
    }

    const handleChange = (e: FormChangeEvent) => {
        const clone = [...form.warranties]
        const index = parseInt(e.target.dataset?.index as string)
        const key = e.target.name as keyof UploadWarrantyDataType
        if (clone[index][key] !== e.target.value) {
            clone[index][key] = e.target.value as string
            props.onChange({ target: { name: 'warranties', value: clone } })
        }
    }

    const handleErrorInfoClick = (row: UploadWarrantiesRowDataType) => {
        setSelectedRow(row)
        const rowErrors = errorsMemo[row.index as number]
        if (rowErrors.address_conflict) {
            setShowDuplicatePopup(true)
        } else if (rowErrors.address_invalid) {
            setShowUnverifiedPopup(true)
        }
    }

    const handleDeleteRow = (index: number) => {
        const payloadClone = [...form.warranties]
        payloadClone.splice(index, 1)
        props.onChange({ target: { name: 'warranties', value: payloadClone } })
        setShowDeletePopup(false)
        setSelectedRow(null)
    }

    const rows = useMemo<TableRowType[]>(() =>
        form.warranties.reduce((acc: TableRowType[], item, index: number) => {
            const rowErrors = errorsMemo[index]
            const rowWarnings = warningsMemo[index]
            if (tab === 'clean' && (rowErrors || rowWarnings)) return acc
            if (tab === 'errored' && !rowErrors) return acc
            if (tab === 'warned' && !rowWarnings) return acc
            return [...acc, {
                id: 1 + index,
                action: <Button
                    tooltip="Delete this row"
                    design="btn-link"
                    type="button"
                    onClick={() => {
                        setShowDeletePopup(true)
                        setSelectedRow({ index, ...item })
                    }}
                >
                    <IconTrash02 size="sm" className="stroke-gray-700"/>
                </Button>,
                ...Object.keys(item).reduce((acc, key) => {
                    const error = rowErrors?.[key]
                    const warning = rowWarnings?.[key]
                    const Component = ['street_address', 'address'].includes(key) ? AddressInput : Input
                    return {
                        ...acc,
                        [key]: (focusedCell.index === index && focusedCell.key === key) || error || warning
                            ? <Component
                                id={`row-${1 + index}-${key}-input`}
                                value={item[key as keyof UploadWarrantiesRowDataType] as string}
                                data-index={index.toString()}
                                name={key}
                                onChange={handleChange}
                                errors={!!error}
                                warnings={!!warning}
                                transparent
                                tooltip={error
                                    ? error
                                    : warning
                                        ? warning
                                        : undefined}
                                postIcon={
                                    (rowErrors?.address_conflict || rowErrors?.address_invalid) &&
                                        ['street_address', 'address'].includes(key)
                                        ? <Button
                                            tooltip="Click icon to fix this error"
                                            type="button"
                                            design="btn-link"
                                            onClick={() => handleErrorInfoClick({ index, ...item })}
                                        >
                                            <IconHelpCircle size="sm" className="stroke-gray-400"/>
                                        </Button>
                                        : rowWarnings?.missing_tesla_din && key === 'system_id'
                                            ? <Tooltip
                                                interactive={true}
                                                content={<span className="flex gap-1">
                                                    Need help? <Button
                                                        className="font-semibold text-white text-xs"
                                                        design="btn-link"
                                                        onClick={() => {
                                                            setShowSystemIdPopup(true)
                                                        }}
                                                    >
                                                        View Tesla DIN Instructions
                                                    </Button>
                                                </span>}
                                            >
                                                <IconAlertTriangle size="sm" className="stroke-orange-400"/>
                                            </Tooltip>
                                            : undefined
                                }
                            />
                            : <button
                                className="px-3.5 py-2.5 border border-transparent w-full text-left cursor-text font-body"
                                type="button"
                                onClick={() => {
                                    flushSync(() => setFocusedCell({ index, key }))
                                    const input = document.getElementById(`row-${1 + index}-${key}-input`) as HTMLInputElement
                                    input.focus()
                                }}
                            >
                                {item[key as keyof UploadWarrantiesRowDataType]}
                            </button>
                    }
                }, {})
            }]
        }, []), [form.warranties, errorsMemo, tab, focusedCell])

    return <>
        <Form onSubmit={props.onNext}>
            <p className="text-sm pt-5">
                Our system checks the values of each row for any errors. <br/>
                To edit the data in a cell, click on the cell, make your changes, and then press Enter or click on any
                other cell to apply the modifications.
            </p>

            {!!Object.keys(errorsMemo).length && <Alert type="error" className="mt-7.5">
                Resolve all errors before uploading the spreadsheet.
            </Alert>}

            {Object.values(warningsMemo).some(item => item.missing_tesla_din) &&
                <Alert type="warning" className="mt-7.5">
                    <h4 className="font-semibold">Missing Tesla Device Identification Number (DIN)</h4>
                    <p className="mt-2">
                        You’ve enrolled one or more Tesla components without entering a Device Identification Number
                        (DIN). Although this field isn’t required today, it may be in the future, and we may ask you to
                        provide it later. Adding a DIN now supports monitoring setup, speeds up warranty claim
                        processing, and enhances performance insights we can share with you.
                    </p>
                    <div className="mt-2">
                        Need help? <Button
                            type="button"
                            design="btn-link"
                            className="text-primary-600 inline-flex"
                            onClick={() => {
                                setShowSystemIdPopup(true)
                            }}
                        >
                            View Tesla DIN instructions
                        </Button>
                    </div>
                </Alert>}
            <div className="flex mt-4">
                <Tabs
                    value={tab}
                    onChange={id => setTab(id as TabType)}
                    size="sm"
                    tabs={[
                        { id: 'all', title: `All Rows (${form.warranties.length})` },
                        {
                            id: 'clean',
                            title: `Clean Rows (${form.warranties.length - Object.keys({ ...errorsMemo, ...warningsMemo }).length})`
                        },
                        { id: 'errored', title: `Rows with Errors (${Object.keys(errorsMemo).length})` },
                        { id: 'warned', title: `Rows with Warnings (${Object.keys(warningsMemo).length})` }
                    ]}
                />
            </div>

            {tab === 'errored' && rows.length === 0
                ? <div className="mt-4 text-gray-900 text-sm bg-success-25 border border-gray-200 rounded px-6 py-4 flex flex-col items-center gap-3">
                    <div className="w-14 h-14 bg-success-200 rounded-full flex justify-center items-center border-10 border-success-100">
                        <IconCheckCircleBroken size="lg" className="stroke-success-600"/>
                    </div>

                    <div className="flex flex-col items-center gap-1">
                        <p className="font-semibold">No errors found</p>
                        <p>All good! You have no errors and are ready to upload the data.</p>
                    </div>
                </div>
                : <Table
                    key={tab}
                    className="stripped-table mt-4"
                    options={{
                        sortable: false,
                        useQueryParams: false
                    }}
                    columns={[{
                        field: 'id',
                        title: '#',
                        sticky: true,
                        cellClassName: (row: TableRowType) =>
                            classnames('!px-5 !py-4 border-r', {
                                'bg-error-100 border-error-500 sticky': errorsMemo[row.id as number - 1],
                                'bg-warning-25 border-orange-400 sticky':
                                    !errorsMemo[row.id as number - 1] &&
                                    warningsMemo[row.id as number - 1],
                                'border-gray-200': !errorsMemo[row.id as number - 1]
                            })
                    }, ...columns.filter(({ show }) => show)
                        .map(item => ({
                            field: item.value,
                            title: <div className="px-3.5 py-2.5">{item.title}</div>,
                            className: 'whitespace-nowrap',
                            cellClassName: 'whitespace-nowrap !p-1.5'
                        })), {
                        field: 'action',
                        cellClassName: 'border-l border-gray-200',
                        title: '',
                        sticky: true
                    }]}
                    rows={rows}
                />}

            <div className="flex justify-end gap-3 mt-6">
                <Button
                    design="btn-secondary-gray"
                    onClick={handleClickBack}
                    className="w-36"
                    type="button"
                    onMouseEnter={() => {
                        trackEvent('element_hover', 'warranties', 'Bulk Enrollment Step Confirmation Back Button')
                    }}
                    processing={processing}
                >
                    Back
                </Button>
                <Button
                    onMouseEnter={() => {
                        trackEvent('element_hover', 'warranties', 'Bulk Enrollment Step Confirmation Save Button')
                    }}
                    disabled={!!Object.keys(errorsMemo).length}
                    processing={processing}
                    className="w-36"
                >
                    Save
                </Button>
            </div>
        </Form>
        {selectedRow && showDuplicatePopup && <HandleDuplicatesPopup
            isOpen={showDuplicatePopup}
            cancelButtonText="Remove Duplicate from Upload"
            onConfirm={() => {
                handleChange({
                    target: {
                        name: 'duplicate_override',
                        value: 1,
                        dataset: { index: selectedRow.index?.toString() }
                    }
                })
                setShowDuplicatePopup(false)
            }}
            onCancel={() => {
                handleDeleteRow(selectedRow.index as number)
            }}
            onClose={() => setShowDuplicatePopup(false)}
            warranty={Warranty.fromForm({
                company_id: company.id,
                ...selectedRow
            })}
        />}
        {selectedRow && showUnverifiedPopup && <HandleUnverifiedAddressPopup
            row={selectedRow}
            form={form}
            isOpen={showUnverifiedPopup}
            onChange={props.onChange}
            onClose={() => setShowUnverifiedPopup(false)}
        />}
        {selectedRow && <ConfirmPopup
            isOpen={showDeletePopup}
            cancelButtonText="Cancel Without Changes"
            confirmButtonText="Delete Row"
            className="w-full md:w-auto"
            title={<div className="flex gap-2">
                <IconAlertCircle className="stroke-error-600" size="lg"/>
                Confirm Row Deletion
            </div>}
            description={<p className="flex flex-col gap-4">
                <span>Are you sure you want to delete row #{selectedRow.index as number + 1}?</span>
                <span>
                    This action cannot be undone and will permanently remove all data in this row from your upload.
                </span>
            </p>}
            onConfirm={() => handleDeleteRow(selectedRow.index as number)}
            onCancel={() => {
                setShowDeletePopup(false)
                setSelectedRow(null)
            }}
        />}
        <Popup
            className="w-200"
            open={showSystemIdPopup}
            onClose={() => setShowSystemIdPopup(false)}
        >
            {InverterManufacturer.tesla.systemIdGuide}
        </Popup>
    </>
}

export default StepValidateData
